import { Card, CardBody, CardHeader } from '@/components/card';
import { Skeleton } from '@/components/skeleton';
import { Switch } from '@/components/switch';
import { useToast } from '@/components/toast';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/lib/notifications';
import type { NotificationEventData } from '@/types/api';

/**
 * Email subscriptions, per admin. Everything keeps arriving in the bell
 * regardless; these switches only govern whether it also lands in the inbox.
 * The catalogue is served from the NotificationEvent enum, so a new event
 * appears here with no change to this file.
 */
export function NotificationsTab() {
  const prefs = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();
  const toast = useToast();

  async function toggle(event: NotificationEventData, next: boolean) {
    try {
      await update.mutateAsync({ [event.key]: next });
    } catch {
      toast.error('Could not save that preference');
    }
  }

  if (prefs.isLoading) {
    return (
      <Card>
        <CardBody className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardBody>
      </Card>
    );
  }

  if (!prefs.data) {
    return (
      <Card>
        <CardBody className="text-sm text-muted-foreground">
          Could not load your notification settings.
        </CardBody>
      </Card>
    );
  }

  const { events, subscribed } = prefs.data;
  const groups = [...new Set(events.map((e) => e.group))];

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Email notifications</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Pick what reaches your inbox. Everything shows up in the bell either way, and this
            applies to you only, not to the rest of the team.
          </p>
        </CardHeader>
      </Card>

      {groups.map((group) => (
        <Card key={group}>
          <CardHeader>
            <h3 className="text-sm font-semibold">{group}</h3>
          </CardHeader>
          <CardBody className="divide-y divide-border">
            {events
              .filter((event) => event.group === group)
              .map((event) => (
                <div key={event.key} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{event.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{event.description}</p>
                  </div>
                  <Switch
                    checked={subscribed[event.key] ?? event.defaults_to_email}
                    onChange={(next) => toggle(event, next)}
                    disabled={update.isPending}
                    label={`Email me about ${event.label.toLowerCase()}`}
                  />
                </div>
              ))}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}
